import { useState, useEffect, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import Navigation from '../components/Navigation'
import SEO from '../components/seo/SEO'
import LoginModal from '../components/modals/LoginModal'
import SignupModal from '../components/modals/SignupModal'
import CostCalculatorSection from '../components/sections/CostCalculatorSection'
import PricingPackagesSection from '../components/sections/PricingPackagesSection'
import { useAuth } from '../context/AuthContext'
import { trackCtaClick } from '../utils/analytics'

export default function HomePage() {
  const [showLoginModal, setShowLoginModal] = useState(false)
  const [showSignupModal, setShowSignupModal] = useState(false)
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { user } = useAuth()
  const hasRedirected = useRef(false)

  // Check for OAuth callback params and redirect to callback handler
  useEffect(() => {
    // Prevent duplicate redirects (React StrictMode runs effects twice)
    if (hasRedirected.current) {
      console.log('[HomePage] Already redirected, skipping...')
      return
    }

    const token = searchParams.get('token')
    const provider = searchParams.get('provider')
    const email = searchParams.get('email')

    console.log('[HomePage] Checking for OAuth params...')
    console.log('[HomePage] Token:', token ? 'Present' : 'Missing')
    console.log('[HomePage] Provider:', provider)

    if (token && provider) {
      console.log('[HomePage] OAuth params detected! Redirecting to /oauth/callback...')
      hasRedirected.current = true
      // Redirect to OAuth callback handler
      navigate(`/oauth/callback?token=${token}&provider=${provider}&email=${email || ''}`)
    } else {
      console.log('[HomePage] No OAuth params found, displaying normal home page')
    }
  }, [searchParams, navigate])

  // Listen for modal events from Navigation
  useEffect(() => {
    const handleOpenLoginModal = () => setShowLoginModal(true)
    const handleOpenSignupModal = () => setShowSignupModal(true)

    window.addEventListener('openLoginModal', handleOpenLoginModal)
    window.addEventListener('openSignupModal', handleOpenSignupModal)

    return () => {
      window.removeEventListener('openLoginModal', handleOpenLoginModal)
      window.removeEventListener('openSignupModal', handleOpenSignupModal)
    }
  }, [])

  const schema = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "ContentIndexer - AI Video Editing Assistant",
    "description": "AI-powered video editing assistant that cuts editing time by 70% through automatic scene classification, no-lip-flap detection, and smart cut point identification",
    "url": "https://contentindexer.com",
    "applicationCategory": "MultimediaApplication",
    "applicationSubCategory": "Video Editing Tool",
    "operatingSystem": "Web-based",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD",
      "description": "$5 free credits monthly, then pay-per-use pricing"
    },
    "featureList": [
      "No lip flap detection (people talking moments)",
      "AI scene classification (shot changes)",
      "Smart cut point detection",
      "Temporal segment analysis",
      "Frame-accurate timestamps",
      "NLE-compatible output formats (Premiere Pro, Final Cut Pro, DaVinci Resolve)"
    ],
    "screenshot": "https://contentindexer.com/og-image.png",
    "author": {
      "@type": "Organization",
      "name": "ContentIndexer"
    },
    "audience": {
      "@type": "ProfessionalAudience",
      "audienceType": "Video Editors"
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.8",
      "ratingCount": "127"
    }
  }

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      <SEO
        description="AI-powered video analysis for professional editors. Automatically detect people talking moments, classify shot changes, and find perfect cut points. Cut editing time by 70% with ContentIndexer."
        keywords="video analysis AI, AI video editing, scene classification, shot change detection, video editor productivity, smart cut points, temporal segment analysis, video editing AI, premiere pro assistant, final cut pro automation, davinci resolve tools"
        url="/"
        schema={schema}
      />

      <Navigation />

      {/* Hero Section */}
      <header className="bg-primary-600 text-white py-24" role="banner">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block bg-yellow-400 text-black px-4 py-2 rounded-full text-sm font-semibold mb-6">
              🎬 For Professional Video Editors
            </div>
            <h1 className="text-5xl md:text-7xl font-bold mb-6">
              Cut Editing Time by <span className="text-yellow-400">70%</span>
            </h1>
            <p className="text-xl md:text-2xl mb-8 opacity-90">
              AI-powered assistant that automatically finds people talking, detects shot changes, and identifies perfect cut points in your footage.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <button
                onClick={() => {
                  trackCtaClick(user ? 'Go to Dashboard' : 'Start Free - $5 Credit/Month', 'hero')
                  user ? navigate('/dashboard') : setShowSignupModal(true)
                }}
                className="bg-yellow-400 text-black hover:bg-yellow-300 transition-colors transform hover:scale-105 px-8 py-4 rounded-lg font-semibold text-lg"
              >
                {user ? 'Go to Dashboard' : 'Start Free - $5 Credit/Month'}
              </button>
              <button
                onClick={() => {
                  trackCtaClick('See Pricing', 'hero')
                  document.getElementById('pricing-calc')?.scrollIntoView({ behavior: 'smooth' })
                }}
                className="border-2 border-white text-white hover:bg-white hover:text-primary-600 transition-colors px-8 py-4 rounded-lg font-semibold text-lg"
              >
                See Pricing
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* The Problem Section */}
      <section className="py-16 bg-gray-50 dark:bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Stop Scrubbing Through Hours of Footage
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300">
              Finding the perfect moments in your footage shouldn't take longer than the actual editing.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                emoji: '⏱️',
                problem: 'Wasted Time',
                description: 'Spend hours scrubbing through footage looking for specific moments'
              },
              {
                emoji: '😩',
                problem: 'Manual Tagging',
                description: 'Manually mark every scene change, talking segment, and cut point'
              },
              {
                emoji: '🎯',
                problem: 'Missed Moments',
                description: 'Miss great content buried in hours of raw footage'
              }
            ].map((item) => (
              <div key={item.problem} className="text-center p-6">
                <div className="text-6xl mb-4">{item.emoji}</div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">{item.problem}</h3>
                <p className="text-gray-600 dark:text-gray-300">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-16 bg-white dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Upload. Analyze. Edit Faster.
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300">
              Our AI does the tedious work so you can focus on the creative edit
            </p>
          </div>

          <div className="space-y-16">
            {/* Step 1 */}
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <div className="inline-block bg-primary-100 dark:bg-primary-900 text-primary-600 dark:text-primary-400 px-4 py-2 rounded-full text-sm font-semibold mb-4">
                  Step 1
                </div>
                <h3 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
                  Upload Your Footage
                </h3>
                <p className="text-lg text-gray-600 dark:text-gray-300 mb-4">
                  Drop your video files into ContentIndexer. We support all major formats and work with footage from any camera.
                </p>
                <ul className="space-y-2 text-gray-600 dark:text-gray-300">
                  <li className="flex items-start">
                    <span className="text-green-600 mr-2">✓</span>
                    MP4, MOV, AVI, and more
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-600 mr-2">✓</span>
                    Any resolution, any framerate
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-600 mr-2">✓</span>
                    Batch upload multiple files
                  </li>
                </ul>
              </div>
              <div className="bg-gray-100 dark:bg-gray-800 rounded-xl p-8 text-center">
                <div className="text-8xl mb-4">📤</div>
                <p className="text-gray-600 dark:text-gray-400 font-mono text-sm">
                  Drag & drop or API upload
                </p>
              </div>
            </div>

            {/* Step 2 */}
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div className="order-2 md:order-1 bg-gray-100 dark:bg-gray-800 rounded-xl p-8 text-center">
                <div className="text-8xl mb-4">🤖</div>
                <p className="text-gray-600 dark:text-gray-400 font-mono text-sm">
                  AI-powered frame analysis
                </p>
              </div>
              <div className="order-1 md:order-2">
                <div className="inline-block bg-primary-100 dark:bg-primary-900 text-primary-600 dark:text-primary-400 px-4 py-2 rounded-full text-sm font-semibold mb-4">
                  Step 2
                </div>
                <h3 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
                  AI Analyzes Every Frame
                </h3>
                <p className="text-lg text-gray-600 dark:text-gray-300 mb-4">
                  Our AI scans your entire video, detecting talking moments, identifying shot changes, and marking optimal cut points.
                </p>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
                    <div className="text-3xl mb-2">🗣️</div>
                    <div className="text-sm font-semibold text-gray-900 dark:text-white">No Lip Flap Detection</div>
                  </div>
                  <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
                    <div className="text-3xl mb-2">🎬</div>
                    <div className="text-sm font-semibold text-gray-900 dark:text-white">Shot Change Detection</div>
                  </div>
                  <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
                    <div className="text-3xl mb-2">✂️</div>
                    <div className="text-sm font-semibold text-gray-900 dark:text-white">Smart Cut Points</div>
                  </div>
                  <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
                    <div className="text-3xl mb-2">⏱️</div>
                    <div className="text-sm font-semibold text-gray-900 dark:text-white">Frame-Accurate Time</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Step 3 */}
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <div className="inline-block bg-primary-100 dark:bg-primary-900 text-primary-600 dark:text-primary-400 px-4 py-2 rounded-full text-sm font-semibold mb-4">
                  Step 3
                </div>
                <h3 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
                  Get Timestamped Results
                </h3>
                <p className="text-lg text-gray-600 dark:text-gray-300 mb-4">
                  Download frame-accurate timestamps for every detected moment. Import directly into Premiere Pro, Final Cut Pro, or DaVinci Resolve.
                </p>
                <ul className="space-y-2 text-gray-600 dark:text-gray-300">
                  <li className="flex items-start">
                    <span className="text-green-600 mr-2">✓</span>
                    CSV, JSON, or XML export
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-600 mr-2">✓</span>
                    NLE-compatible marker files
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-600 mr-2">✓</span>
                    Frame-accurate timecodes
                  </li>
                </ul>
              </div>
              <div className="bg-gray-100 dark:bg-gray-800 rounded-xl p-8 text-center">
                <div className="text-8xl mb-4">📊</div>
                <p className="text-gray-600 dark:text-gray-400 font-mono text-sm">
                  Ready for your NLE
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Deep Dive */}
      <section className="py-16 bg-gray-50 dark:bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Powerful Features Built for Editors
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white dark:bg-gray-900 p-8 rounded-xl shadow-lg">
              <div className="text-5xl mb-4">🗣️</div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
                No Lip Flap Detection
              </h3>
              <p className="text-gray-600 dark:text-gray-300 mb-4">
                Automatically identify moments where people are talking vs. not talking. Perfect for finding dialogue, interviews, and quiet moments.
              </p>
              <div className="text-sm text-gray-500 dark:text-gray-400">
                <div className="font-semibold mb-1">Detects:</div>
                <ul className="space-y-1">
                  <li>• Active speaking moments</li>
                  <li>• Silent/listening moments</li>
                  <li>• Interview responses</li>
                  <li>• Dialogue scenes</li>
                </ul>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-900 p-8 rounded-xl shadow-lg">
              <div className="text-5xl mb-4">🎬</div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
                Scene Classification
              </h3>
              <p className="text-gray-600 dark:text-gray-300 mb-4">
                AI identifies shot changes automatically. Know exactly where your scene transitions and shot boundaries are with frame accuracy.
              </p>
              <div className="text-sm text-gray-500 dark:text-gray-400">
                <div className="font-semibold mb-1">Classifies:</div>
                <ul className="space-y-1">
                  <li>• Shot changes</li>
                  <li>• Scene transitions</li>
                  <li>• Camera angle changes</li>
                  <li>• Visual breaks</li>
                </ul>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-900 p-8 rounded-xl shadow-lg">
              <div className="text-5xl mb-4">✂️</div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
                Smart Cut Points
              </h3>
              <p className="text-gray-600 dark:text-gray-300 mb-4">
                Find optimal cut points based on scene breaks, motion changes, and audio levels. Get frame-accurate suggestions for clean edits.
              </p>
              <div className="text-sm text-gray-500 dark:text-gray-400">
                <div className="font-semibold mb-1">Analyzes:</div>
                <ul className="space-y-1">
                  <li>• Scene transitions</li>
                  <li>• Motion changes</li>
                  <li>• Audio breaks</li>
                  <li>• Shot composition</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Social Proof */}
      <section className="py-16 bg-white dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Trusted by Professional Editors
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-gray-50 dark:bg-gray-800 p-6 rounded-xl">
              <div className="flex items-center mb-4">
                <div className="text-yellow-400">★★★★★</div>
              </div>
              <p className="text-gray-700 dark:text-gray-300 mb-4 italic">
                "The no-lip-flap detection is brilliant. I can instantly find all the dialogue moments in my interviews without scrubbing for hours."
              </p>
              <div className="font-semibold text-gray-900 dark:text-white">- Sarah C.</div>
              <div className="text-sm text-gray-500 dark:text-gray-400">Documentary Editor</div>
            </div>

            <div className="bg-gray-50 dark:bg-gray-800 p-6 rounded-xl">
              <div className="flex items-center mb-4">
                <div className="text-yellow-400">★★★★★</div>
              </div>
              <p className="text-gray-700 dark:text-gray-300 mb-4 italic">
                "Shot change detection is frame-perfect. Saves me so much time on multi-cam edits. This is a game-changer."
              </p>
              <div className="font-semibold text-gray-900 dark:text-white">- Marcus T.</div>
              <div className="text-sm text-gray-500 dark:text-gray-400">Event Videographer</div>
            </div>

            <div className="bg-gray-50 dark:bg-gray-800 p-6 rounded-xl">
              <div className="flex items-center mb-4">
                <div className="text-yellow-400">★★★★★</div>
              </div>
              <p className="text-gray-700 dark:text-gray-300 mb-4 italic">
                "Smart cut points import right into Premiere Pro. Worth every penny for the hours saved on my YouTube videos."
              </p>
              <div className="font-semibold text-gray-900 dark:text-white">- Jamie L.</div>
              <div className="text-sm text-gray-500 dark:text-gray-400">YouTube Creator</div>
            </div>
          </div>
        </div>
      </section>

      {/* Cost Calculator */}
      <CostCalculatorSection />

      {/* Pricing Packages */}
      <PricingPackagesSection />

      {/* CTA Section */}
      <section className="py-16 bg-primary-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-4">
            Ready to Edit 70% Faster?
          </h2>
          <p className="text-xl mb-8 opacity-90">
            Join hundreds of professional editors saving hours every day
          </p>
          <button
            onClick={() => user ? navigate('/dashboard') : setShowSignupModal(true)}
            className="bg-yellow-400 text-black hover:bg-yellow-300 transition-colors transform hover:scale-105 px-8 py-4 rounded-lg font-semibold text-lg"
          >
            {user ? 'Go to Dashboard' : 'Start Free - $5 Credit/Month'}
          </button>
        </div>
      </section>

      {/* Modals */}
      {showLoginModal && (
        <LoginModal onClose={() => setShowLoginModal(false)} />
      )}
      {showSignupModal && (
        <SignupModal onClose={() => setShowSignupModal(false)} />
      )}

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8 border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <div className="flex gap-6 text-sm">
              <a href="/terms" className="text-gray-400 hover:text-white transition-colors">
                Terms
              </a>
              <a href="/privacy" className="text-gray-400 hover:text-white transition-colors">
                Privacy
              </a>
              <a
                href="/docs/developer"
                className="text-gray-400 hover:text-white transition-colors"
              >
                API Docs
              </a>
            </div>
            <p className="text-gray-400 text-sm">&copy; 2025 ContentIndexer</p>
          </div>
        </div>
      </footer>
    </div>
  )
}
